import { Button, Icon, IconButton, ProgressBar } from '@platform/components';
import cx from 'classix';
import { useState } from 'react';
import { ApplicationSuccessIcon } from './components/ApplicationSuccessIcon';
import LeftSection from './images/Default.png';
import { SaveAndExitModal } from './SaveAndExitModal';
import { ProfileSaveAndExitModal } from '../profile/ProfileSaveAndExitModal';

type ApplicationBackgroundProps = ApplicationFooterProps &
  ApplicationStepperProps & {
    onSubmit: () => void;
    isOrganization?: boolean;
    isEditProfile?: boolean;
    backgroundImage?: string;
  } & React.PropsWithChildren;

export const ApplicationBackground = (props: ApplicationBackgroundProps) => {
  const { isOrganization = false, backgroundImage, isEditProfile = false } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [isProfileEditOpen, setIsProfileEditOpen] = useState(false);

  const onSaveAndExit = () => {
    if (isEditProfile) {
      setIsProfileEditOpen(true);
    } else {
      setIsOpen(true);
    }
  };

  return (
    <form
      className='w-full h-[100vh] md:max-h-[100vh] flex flex-col overflow-y-auto md:overflow-y-auto'
      onSubmit={props.onSubmit}
    >
      {isOrganization && !isEditProfile && (
        <OrganizationApplicationStepper {...props} onSaveAndExit={onSaveAndExit} />
      )}
      {!isOrganization && !isEditProfile && (
        <ApplicationStepper {...props} onSaveAndExit={onSaveAndExit} />
      )}
      {isEditProfile && !isOrganization && (
        <EditProfileStepper {...props} onSaveAndExit={onSaveAndExit} />
      )}
      <div
        className={cx(
          'w-full flex flex-row h-full',
          // we hide steppers on profile edit, use full view height in that case
          isEditProfile && isOrganization ? 'md:h-[100vh]' : 'md:h-[calc(100vh-168px)]',
        )}
      >
        <div className='hidden h-full md:block w-1/2 bg-gray-50'>
          <div
            className='hidden h-full w-full md:flex justify-start items-start'
            style={{
              backgroundImage: `url(${backgroundImage ?? LeftSection})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundColor: 'white',
            }}
          />
        </div>
        <div className='w-full h-full md:w-1/2 md:px-8 md:py-4 flex justify-center overflow-y-scroll '>
          {props.children}
        </div>
      </div>
      <SaveAndExitModal
        isOpen={isOpen}
        closeModal={() => setIsOpen(false)}
        isRep={isOrganization}
      />
      <ProfileSaveAndExitModal
        onSubmit={props.onSubmit}
        isOpen={isProfileEditOpen}
        closeModal={() => setIsProfileEditOpen(false)}
        isRep={props.isOrganization}
      />
      <ApplicationFooter {...props} openSaveAndExitModal={onSaveAndExit} />
    </form>
  );
};

type ApplicationStepperItemProps = {
  title: string;
  progress: number; // 0-100

  hideIcon?: boolean;
};

const ApplicationStepperItem = (props: ApplicationStepperItemProps) => {
  const { title, progress, hideIcon } = props;

  return (
    <div className='flex flex-col gap-2 w-full'>
      <div className='flex flex-row gap-2 items-center justify-center text-md font-semibold w-full'>
        {progress !== 100 && !hideIcon && (
          <Icon type={'check-circle'} size={'24'} additionalClassNames={'text-gray-400'} />
        )}
        {progress === 100 && !hideIcon && <ApplicationSuccessIcon />}
        {title}
      </div>
      <div className='grow-1 h-2 bg-gray-100'>
        <ProgressBar valueFormat={'percent'} value={progress} valueLocation='none' flatBar={true} />
      </div>
    </div>
  );
};

type ApplicationStepperProps = {
  progress?: number; // 0 - 400
  onSaveAndExit?: () => void;
};

const ProfessionalApplicationSteps = ['Program', 'Profile', 'Skills', 'Final details'];

const ApplicationStepper = (props: ApplicationStepperProps) => {
  const { progress = 0, onSaveAndExit } = props;

  const programProgress = Math.min(progress, 100);
  const profileProgress = Math.min(Math.max(progress - 100, 0), 100);
  const skillsProgress = Math.min(Math.max(progress - 200, 0), 100);
  const finalDetailsProgress = Math.min(Math.max(progress - 300, 0), 100);

  const step = Math.max(Math.ceil(progress / 100), 1);
  const totalSteps = 4; // Total number of steps in the application
  const progressPercentage = (step / totalSteps) * 100;

  return (
    <>
      <div className='hidden md:flex flex-row gap-4 items-center justify-center w-full h-[100px] md:h-[80px] pt-6 pb-4'>
        <ApplicationStepperItem
          title={ProfessionalApplicationSteps[0]}
          progress={programProgress}
        />
        <ApplicationStepperItem
          title={ProfessionalApplicationSteps[1]}
          progress={profileProgress}
        />
        <ApplicationStepperItem title={ProfessionalApplicationSteps[2]} progress={skillsProgress} />
        <ApplicationStepperItem
          title={ProfessionalApplicationSteps[3]}
          progress={finalDetailsProgress}
        />
      </div>
      <div className='md:hidden flex flex-col gap-4 w-full h-[100px] md:h-[80px] pt-6 pb-4'>
        <div className='flex flex-row gap-2 items-center justify-between px-4'>
          <div className='text-lg font-semibold'>
            Step {step} of {totalSteps}: {ProfessionalApplicationSteps[step - 1]}
          </div>
          <IconButton
            type='button'
            icon='x-close'
            size='md'
            additionalClassnames='text-gray-300'
            variant='tertiary-gray'
            onPress={onSaveAndExit}
          />
        </div>
        <ProgressBar valueFormat={'percent'} value={progressPercentage} valueLocation='none' />
      </div>
    </>
  );
};

const OrganizationApplicationSteps = [
  'Org profile',
  'Operations',
  'Challenges',
  'Your Profile',
  'Final details',
];

const OrganizationApplicationStepper = (props: ApplicationStepperProps) => {
  const { progress = 0, onSaveAndExit } = props;

  const orgProfileProgress = Math.min(progress, 100);
  const operationsProgress = Math.min(Math.max(progress - 100, 0), 100);
  const challengesProgress = Math.min(Math.max(progress - 200, 0), 100);
  const yourProfileProgress = Math.min(Math.max(progress - 300, 0), 100);
  const finalDetailsProgress = Math.min(Math.max(progress - 400, 0), 100);

  const step = Math.max(Math.ceil(progress / 100), 1); // Ensure at least step 1 is shown
  const totalSteps = 5; // Total number of steps in the organization application
  const progressPercentage = (step / totalSteps) * 100;

  return (
    <>
      <div className='hidden md:flex flex-row gap-4 items-center justify-center w-full h-[100px] md:h-[80px] pt-6 pb-4'>
        <ApplicationStepperItem
          title={OrganizationApplicationSteps[0]}
          progress={orgProfileProgress}
        />
        <ApplicationStepperItem
          title={OrganizationApplicationSteps[1]}
          progress={operationsProgress}
        />
        <ApplicationStepperItem
          title={OrganizationApplicationSteps[2]}
          progress={challengesProgress}
        />
        <ApplicationStepperItem
          title={OrganizationApplicationSteps[3]}
          progress={yourProfileProgress}
        />
        <ApplicationStepperItem
          title={OrganizationApplicationSteps[4]}
          progress={finalDetailsProgress}
        />
      </div>
      <div className='md:hidden flex flex-col gap-4 w-full h-[100px] md:h-[80px] pt-6 pb-4'>
        <div className='flex flex-row gap-2 items-center justify-between px-4'>
          <div className='text-lg font-semibold'>
            Step {step} of {totalSteps}: {OrganizationApplicationSteps[step - 1]}
          </div>
          <IconButton
            type='button'
            icon='x-close'
            size='md'
            additionalClassnames='text-gray-300'
            variant='tertiary-gray'
            onPress={onSaveAndExit}
          />
        </div>
        <ProgressBar valueFormat={'percent'} value={progressPercentage} valueLocation='none' />
      </div>
    </>
  );
};

const EditProfileSteps = ['Profile', 'Skills'];

const EditProfileStepper = (props: ApplicationStepperProps) => {
  const { progress = 0, onSaveAndExit } = props;

  const profileProgress = Math.min(progress, 100);
  const skillsProgress = Math.min(Math.max(progress - 100, 0), 100);

  const step = Math.max(Math.ceil(progress / 100), 1); // Ensure at least step 1 is shown
  const totalSteps = 2;
  const progressPercentage = (step / totalSteps) * 100;

  return (
    <>
      <div className='hidden md:flex flex-row gap-4 items-center justify-center w-full h-[100px] md:h-[80px] pt-6 pb-4'>
        <ApplicationStepperItem
          title={EditProfileSteps[0]}
          progress={profileProgress}
          hideIcon={true}
        />
        <ApplicationStepperItem
          title={EditProfileSteps[1]}
          progress={skillsProgress}
          hideIcon={true}
        />
      </div>
      <div className='md:hidden flex flex-col gap-4 w-full h-[100px] md:h-[80px] pt-6 pb-4'>
        <div className='flex flex-row gap-2 items-center justify-between px-4'>
          <div className='text-lg font-semibold'>
            Step {step} of {totalSteps}: {EditProfileSteps[step - 1]}
          </div>
          <IconButton
            type='button'
            icon='x-close'
            size='md'
            additionalClassnames='text-gray-300'
            variant='tertiary-gray'
            onPress={onSaveAndExit}
          />
        </div>
        <ProgressBar valueFormat={'percent'} value={progressPercentage} valueLocation='none' />
      </div>
    </>
  );
};

type ApplicationFooterProps = {
  onBack: () => void;
  openSaveAndExitModal?: () => void;
};

const ApplicationFooter = (props: ApplicationFooterProps) => {
  const { onBack, openSaveAndExitModal } = props;

  return (
    <div className='flex flex-row justify-between items-center w-full bg-gray-50 py-6 px-4 md:px-8 min-h-[88px] h-[88px]'>
      {/* leave div but remove button on profile edit, perserve right justification for nav buttons */}
      <div className='hidden md:block'>
        <Button variant='link-color' size='md' onPress={openSaveAndExitModal}>
          Save & exit
        </Button>
      </div>
      <div className='hidden md:flex flex-row gap-4 justify-between'>
        <Button variant='secondary-gray' additionalClassnames='w-[75px]' size='md' onPress={onBack}>
          Back
        </Button>
        <Button variant='primary' size='md' type='submit' additionalClassnames='w-[107px]'>
          Continue
        </Button>
      </div>
      <div className='flex md:hidden flex-row gap-4 justify-between w-full'>
        <Button variant='secondary-gray' size='md' onPress={onBack} additionalClassnames='w-1/2'>
          Back
        </Button>
        <Button variant='primary' size='md' type='submit' additionalClassnames='w-1/2'>
          Continue
        </Button>
      </div>
    </div>
  );
};
